// components/advertiser-wallet/ReportView.tsx
import React from "react";
import { ChevronDown } from "lucide-react";

import {
  TopRewardSpend,
  WalletTransaction,
  RewardStatusFilter,
  RewardOption,
} from "./types";
import {
  PERIOD_DAYS_OPTIONS,
  REWARD_STATUS_OPTIONS,
  REWARD_SORT_OPTIONS,
} from "./constants";
import SpendTrendChart from "../../../components/chart/spend-trend";
import Pagination from "../../../components/pagination";
import StatCard from "../../../components/dashboard/stat-card-wallet";
import { formatCurrency, formatDateLong } from "../../../utils/function";
import { DateRange, PeriodDropdown, PeriodValue } from "../../../components/period-filter";

interface ReportViewProps {
  reportStats: any[];
  spendTrendData: { day: string; value: number }[];
  insightMessage?: string | null;
  periodDays: number;
  onPeriodDaysChange: (days: number) => void;

  rewardSpend: TopRewardSpend[];
  rewardOptions: RewardOption[];
  rewardStatusFilter: RewardStatusFilter;
  onRewardStatusFilterChange: (value: RewardStatusFilter) => void;
  rewardIdFilter: string;
  onRewardIdFilterChange: (value: string) => void;
  rewardPeriodFilter: PeriodValue;
  onRewardPeriodFilterChange: (value: PeriodValue) => void;
  rewardCustomRange: DateRange;
  onRewardCustomRangeChange: (range: DateRange) => void;
  rewardSortValue: string;
  onRewardSortValueChange: (value: string) => void;
  rewardSpendPage: number;
  rewardSpendTotalPages: number;
  rewardSpendLoading: boolean;
  onRewardSpendPageChange: (page: number) => void;

  transactions: WalletTransaction[];
  runwayDays: number;
  formatNaira: (n: number) => string;
  transactionsPage: number;
  transactionsTotalPages: number;
  transactionsLoading: boolean;
  onTransactionsPageChange: (page: number) => void;
}

interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

const FilterSelect: React.FC<FilterSelectProps> = ({ value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none flex items-center gap-1.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-full pl-3.5 pr-8 py-2 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
  </div>
);

interface DropdownProps {
  label: string;
}

const Dropdown: React.FC<DropdownProps> = ({ label }) => (
  <button className="flex items-center gap-1.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-full px-3.5 py-2 whitespace-nowrap">
    {label}
    <ChevronDown size={14} />
  </button>
);

const ReportView: React.FC<ReportViewProps> = ({
  reportStats,
  spendTrendData,
  insightMessage,
  periodDays,
  onPeriodDaysChange,
  rewardSpend,
  rewardOptions,
  rewardStatusFilter,
  onRewardStatusFilterChange,
  rewardIdFilter,
  onRewardIdFilterChange,
  rewardPeriodFilter,
  onRewardPeriodFilterChange,
  rewardCustomRange,
  onRewardCustomRangeChange,
  rewardSortValue,
  onRewardSortValueChange,
  rewardSpendPage,
  rewardSpendTotalPages,
  rewardSpendLoading,
  onRewardSpendPageChange,
  transactions,
  formatNaira,
  transactionsPage,
  transactionsTotalPages,
  transactionsLoading,
  onTransactionsPageChange,
}) => (
  <>
    {/* Detailed Reward Trends */}
    <div className=" p-5 sm:p-6 mb-4">
      <div className="flex items-center justify-between mb-5 gap-3">
        <h2 className="font-semibold text-black text-base">Detailed Reward Trends</h2>
        <FilterSelect
          value={periodDays.toString()}
          onChange={(value) => onPeriodDaysChange(Number(value))}
          options={PERIOD_DAYS_OPTIONS.map((o) => ({ value: o.value.toString(), label: o.label }))}
        />
      </div>

      <div className="grid grid-cols-2  rounded-2xl lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {reportStats.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      <div className="h- bg-white p-5 sm:p-6 rounded-2xl border">
        <p className="text-sm font-medium text-black mb-1">Spend Trends</p>
        <p className="text-xs text-gray-400 mb-4">
          Track reward spending across your active programs over time
        </p>


        <SpendTrendChart data={spendTrendData} />
        <div className="flex gap-2 items-start bg-gray-50 rounded-xl px-3.5 py-3 mt-4 text-xs text-gray-500">
          <span className="text-emerald-500 mt-0.5">✦</span>
          <span>
            <strong className="text-black">Insight:</strong>{" "}
            {insightMessage || "No spend insight available for this period yet."}
          </span>
        </div>
      </div>
    </div>

    {/* Reward Spend table */}
    <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 mb-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-semibold text-black text-base">Reward Spend</h2>
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            value={rewardStatusFilter}
            onChange={(value) => onRewardStatusFilterChange(value as RewardStatusFilter)}
            options={REWARD_STATUS_OPTIONS}
          />
          <FilterSelect
            value={rewardIdFilter}
            onChange={onRewardIdFilterChange}
            options={[
              { value: "", label: "All Rewards" },
              ...rewardOptions.map((r) => ({ value: r.id.toString(), label: r.title })),
            ]}
          />
          <FilterSelect
            value={rewardSortValue}
            onChange={onRewardSortValueChange}
            options={REWARD_SORT_OPTIONS}
          />
          <PeriodDropdown
            value={rewardPeriodFilter}
            onChange={onRewardPeriodFilterChange}
            customRange={rewardCustomRange}
            onCustomRangeChange={onRewardCustomRangeChange}
            triggerClassName="flex items-center gap-1.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-full pl-3.5 pr-3 py-2 whitespace-nowrap"
            showDateRangeLabel={false}
          />
        </div>
      </div>
      <p className="text-sm text-gray-400 mb-4">
        Track reward spend across your active programs.
      </p>

      {/* Desktop table header */}
      <div className="hidden sm:grid grid-cols-[1fr_repeat(4,90px)] gap-3 text-xs text-gray-400 px-1 pb-2">
        <span />
        <span>Spend</span>
        <span>Clicks</span>
        <span>Purchases</span>
        <span>Revenue</span>
      </div>

      <div className="divide-y divide-gray-100">
        {rewardSpend.map((item) => (
          <div
            key={item.reward_id}
            className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_repeat(4,90px)] items-center gap-3 py-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              {item.image_url ? (
                <img src={item.image_url} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-sm font-medium shrink-0">
                  {item.business_name?.charAt(0) || "?"}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-black truncate">{item.business_name}</p>
                <p className="text-xs text-gray-400 truncate">{item.title}</p>
                <div className="flex items-center gap-1 text-xs text-emerald-600 mt-0.5">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${item.is_active ? "bg-emerald-500" : "bg-gray-300"
                      }`}
                  />
                  {item.is_active ? "Active Link" : "Inactive Link"}
                </div>
                <p className="text-[11px] text-gray-300 mt-0.5">
                  Created {formatDateLong(new Date(item.created_at))}
                </p>
              </div>
            </div>

            {/* Mobile: stacked metrics */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-right sm:hidden">
              <span className="text-[11px] text-gray-400">Spend</span>
              <span className="text-sm text-emerald-600 font-medium">
                {formatCurrency(item.all_time_spend || 0, "NGN", "symbol")}
              </span>
              {item.revenue !== undefined && (
                <>
                  <span className="text-[11px] text-gray-400">Revenue</span>
                  <span className="text-sm text-emerald-600 font-medium">
                    {formatCurrency(item.revenue || 0, "NGN", "symbol")}
                  </span>
                </>
              )}
            </div>

            {/* Desktop columns */}
            <span className="hidden sm:block text-sm text-emerald-600 font-medium">
              {formatCurrency(item.all_time_spend || 0, "NGN", "symbol")}
            </span>
            <span className="hidden sm:block text-sm text-gray-600">
              {item.clicks ?? "—"}
            </span>
            <span className="hidden sm:block text-sm text-gray-600">
              {item.purchases ?? "—"}
            </span>
            <span className="hidden sm:block text-sm text-emerald-600 font-medium">
              {item.revenue !== undefined ? formatCurrency(item.revenue || 0, "NGN", "symbol") : "—"}
            </span>
          </div>
        ))}
        {rewardSpendLoading && (
          <p className="text-sm text-gray-400 py-6 text-center">Loading reward spend...</p>
        )}
        {!rewardSpendLoading && rewardSpend.length === 0 && (
          <p className="text-sm text-gray-400 py-6 text-center">No reward spend yet.</p>
        )}
      </div>

      {rewardSpendTotalPages > 1 && (
        <Pagination
          currentPage={rewardSpendPage}
          totalPages={rewardSpendTotalPages}
          onPageChange={onRewardSpendPageChange}
          isLoading={rewardSpendLoading}
        />
      )}
    </div>

    {/* Transactions table */}
    <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-semibold text-black text-base">Transactions</h2>
        <div className="flex flex-wrap gap-2">
          <Dropdown label="All Types" />
          <Dropdown label="All Time" />
        </div>
      </div>
      <p className="text-sm text-gray-400 mb-4">
        Track wallet funding, refunds and reward program expenses
      </p>

      {/* Desktop header */}
      <div className="hidden sm:grid grid-cols-[140px_1fr_1fr_100px_110px] gap-3 text-xs text-gray-400 px-1 pb-2 border-b border-gray-100">
        <span>Date and Time</span>
        <span>Type</span>
        <span>Description</span>
        <span>Amount</span>
        <span>Balance After</span>
      </div>

      <div className="divide-y divide-gray-100">
        {transactions.map((tx) => (
          <div
            key={tx.id}
            className="grid sm:grid-cols-[140px_1fr_1fr_100px_110px] gap-1 sm:gap-3 py-3 text-sm"
          >
            <span className="text-xs sm:text-sm text-gray-400 sm:text-gray-700">
              {formatDateLong(new Date(tx.date_time))}
            </span>
            <span className="flex items-center gap-1.5 text-gray-700">
              <span
                className={`w-1.5 h-1.5 rounded-full ${tx.type === "wallet_topup"
                    ? "bg-emerald-500"
                    : tx.type === "reward_refund"
                      ? "bg-blue-500"
                      : "bg-gray-400"
                  }`}
              />
              {tx.type_label}
            </span>
            <span className="text-gray-500 truncate">{tx.description}</span>
            <span className={tx.amount >= 0 ? "text-emerald-600" : "text-orange-500"}>
              {tx.amount >= 0 ? "+" : "-"}
              {formatNaira(Math.abs(tx.amount))}
            </span>
            <span className="text-gray-700">{formatNaira(tx.balance_after)}</span>
          </div>
        ))}
        {transactionsLoading && (
          <p className="text-sm text-gray-400 py-6 text-center">Loading transactions...</p>
        )}
        {!transactionsLoading && transactions.length === 0 && (
          <p className="text-sm text-gray-400 py-6 text-center">No transactions yet.</p>
        )}
      </div>

      {transactionsTotalPages > 1 && (
        <Pagination
          currentPage={transactionsPage}
          totalPages={transactionsTotalPages}
          onPageChange={onTransactionsPageChange}
          isLoading={transactionsLoading}
        />
      )}
    </div>
  </>
);

export default ReportView;
